import type { ChatMessage, ChatRequest, ChatResponse } from '~/types/ai-chat'
// import type { Vehicle } from '~/layers/base/components/ui/types' // Commented out - module not found

export const useAIVehicleChat = () => {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const sendMessage = async (
    message: string,
    conversationHistory: ChatMessage[] = [],
    currentSearchResults?: {
      vehicles: any[]
      query: string
      totalMatches: number
      fullInventory?: any[]
      availableFilters?: any
    }
  ): Promise<ChatResponse | null> => {
    isLoading.value = true
    error.value = null

    try {
      const request: ChatRequest = {
        message,
        conversationHistory,
        currentSearchResults
      }

      const response = await $fetch('/api/ai/vehicle/chat', {
        method: 'POST',
        body: request
      }) as ChatResponse

      return response

    } catch (err) {
      console.error('[useAIVehicleChat] Error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to send message'
      return null
    } finally {
      isLoading.value = false
    }
  }

  const createUserMessage = (content: string): ChatMessage => {
    return {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    }
  }

  const summarizeSearchResults = async (
    vehicles: any[],
    query: string,
    conversationHistory: ChatMessage[] = []
  ): Promise<ChatResponse | null> => {
    const message = `Can you summarize these ${vehicles.length} search results for "${query}"? What are the key highlights and recommendations?`
    
    return await sendMessage(message, conversationHistory, {
      vehicles,
      query,
      totalMatches: vehicles.length
    })
  }

  const refineSearch = async (
    refinementRequest: string,
    currentResults: {
      vehicles: any[]
      query: string
      totalMatches: number
    },
    conversationHistory: ChatMessage[] = []
  ): Promise<ChatResponse | null> => {
    return await sendMessage(refinementRequest, conversationHistory, currentResults)
  }

  const askQuestion = async (
    question: string,
    currentResults?: {
      vehicles: any[]
      query: string
      totalMatches: number
    },
    conversationHistory: ChatMessage[] = []
  ): Promise<ChatResponse | null> => {
    return await sendMessage(question, conversationHistory, currentResults)
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    sendMessage,
    createUserMessage,
    summarizeSearchResults,
    refineSearch,
    askQuestion
  }
}
